// Minimap (ui-flow §6.3; visuals §1: agar's FFA shows none, so "Show minimap" defaults off; it is
// always shown in Battle Royale). Drawn by the renderer on the main canvas in screen space, in the
// bottom-right corner: a translucent panel of the map with a 5 × 5 grid, the BR zone, the top-20
// players (when "Show bigger players on minimap" is on and the room allows it), party members
// (ringed), and your own cells in your colour, sized `max(2 px, r × mapScale)`.

import { MODE_CODE, type MinimapEntry } from '@ballio/shared';
import type { EntityRecord, WorldStore } from '../game/world-store.js';
import type { Settings } from '../settings/store.js';
import type { Frame } from './frame.js';
import { css } from './theme.js';

const TAU = Math.PI * 2;

export const MINIMAP = Object.freeze({
  sizePx: 180,
  smallSizePx: 140,
  /** Below this canvas width (CSS px) the small size is used. */
  smallBelowPx: 700,
  marginPx: 12,
  gridCells: 5,
  minDotPx: 2,
});

/** The minimap's side in CSS px for a canvas width. */
export function minimapSizePx(cssW: number): number {
  return cssW < MINIMAP.smallBelowPx ? MINIMAP.smallSizePx : MINIMAP.sizePx;
}

/** True when the minimap is drawn: the setting, or always in Battle Royale. */
export function minimapShown(settings: Settings, store: Pick<WorldStore, 'info'>): boolean {
  return settings.showMinimap || store.info?.mode === MODE_CODE.battleroyale;
}

export class MinimapPainter {
  /** Draws the minimap (screen transform; `cssW`/`cssH` = canvas CSS size). */
  draw(f: Frame, store: WorldStore, settings: Settings, cssW: number, cssH: number): void {
    const { ctx, bs, map } = f;
    const size = minimapSizePx(cssW) * bs;
    const margin = MINIMAP.marginPx * bs;
    const x0 = cssW * bs - size - margin;
    const y0 = cssH * bs - size - margin;
    const mw = map.maxX - map.minX;
    const mh = map.maxY - map.minY;
    if (!(mw > 0) || !(mh > 0)) return;
    const k = size / Math.max(mw, mh);
    const px = (x: number): number => x0 + (x - map.minX) * k;
    const py = (y: number): number => y0 + (y - map.minY) * k;
    const dark = f.theme.dark;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)';
    ctx.fillRect(x0, y0, mw * k, mh * k);
    ctx.beginPath();
    const n = MINIMAP.gridCells;
    for (let i = 1; i < n; i++) {
      const gx = x0 + (i * mw * k) / n;
      const gy = y0 + (i * mh * k) / n;
      ctx.moveTo(gx, y0);
      ctx.lineTo(gx, y0 + mh * k);
      ctx.moveTo(x0, gy);
      ctx.lineTo(x0 + mw * k, gy);
    }
    ctx.lineWidth = bs;
    ctx.strokeStyle = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)';
    ctx.stroke();
    ctx.strokeRect(x0, y0, mw * k, mh * k);

    const zone = store.match?.value?.zone;
    if (zone && zone.r > 0) {
      ctx.beginPath();
      ctx.arc(px(zone.cx), py(zone.cy), zone.r * k, 0, TAU);
      ctx.lineWidth = 1.5 * bs;
      ctx.strokeStyle = dark ? '#ffffff' : '#000000';
      ctx.stroke();
      if (zone.nextR > 0 && zone.nextR < zone.r) {
        ctx.beginPath();
        ctx.arc(px(zone.nextCx), py(zone.nextCy), zone.nextR * k, 0, TAU);
        ctx.setLineDash([4 * bs, 3 * bs]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    const minDot = MINIMAP.minDotPx * bs;
    const entries: readonly MinimapEntry[] = store.minimap?.value?.entries ?? [];
    const showTop =
      settings.minimapPlayers && (store.info?.minimapPlayersAllowed ?? false) && entries.length > 0;
    const ownId = store.own?.playerId ?? 0;
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i] as MinimapEntry;
      if (e.playerId === ownId) continue;
      const party = e.partyMember;
      if (!party && !(showTop && e.inTop20)) continue;
      const r = Math.max(minDot, e.r * k);
      ctx.beginPath();
      ctx.arc(px(e.x), py(e.y), r, 0, TAU);
      ctx.fillStyle = css(e.color);
      ctx.fill();
      if (party) {
        ctx.lineWidth = 1.5 * bs;
        ctx.strokeStyle = dark ? '#ffffff' : '#000000';
        ctx.stroke();
      }
    }

    const own: readonly EntityRecord[] = store.view.own;
    for (let i = 0; i < own.length; i++) {
      const c = own[i] as EntityRecord;
      ctx.beginPath();
      ctx.arc(px(c.x), py(c.y), Math.max(minDot, c.r * k), 0, TAU);
      ctx.fillStyle = css(c.color);
      ctx.fill();
    }
  }
}
